import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { revalidate } from '@solidjs/router';

import { AddressEditor } from '~/components';
import { submitApprovedBody } from '~/lib/businessLogic';

import type { Subscription } from '~/lib';

export const AddApprovedBody = ( props: { subscription: Subscription } ) => {
	const [approvedBody, setApprovedBody] = createStore( {
		address: {} as Record<string, string>,
		coordinator: { name: '', email: '', telephone: '' },
		deputy: { name: '', email: '', telephone: '' },
	} );

	const [errors, setErrors] = createSignal<Record<string, string>>( {} );
	const [submitting, setSubmitting] = createSignal( false );
	const [submitted, setSubmitted] = createSignal( false );

	const validate = () => {
		const e: Record<string, string> = {};

		if ( !approvedBody.address?.name?.trim() ) {
			e['address'] = 'Company address is required';
		}
		if ( !approvedBody.coordinator.name.trim() ) {
			e['coordinator.name'] = 'Name is required';
		}
		if ( !approvedBody.coordinator.email.trim() ) {
			e['coordinator.email'] = 'Email is required';
		} else if ( !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( approvedBody.coordinator.email ) ) {
			e['coordinator.email'] = 'Enter a valid email address';
		}
		if ( !approvedBody.coordinator.telephone.trim() ) {
			e['coordinator.telephone'] = 'Telephone is required';
		}

		if ( approvedBody.deputy.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( approvedBody.deputy.email ) ) {
			e['deputy.email'] = 'Enter a valid email address';
		}

		setErrors( e );
		return Object.keys( e ).length === 0;
	};

	const handleSubmit = async ( e: Event ) => {
		e.preventDefault();

		if ( !validate() ) return;

		setSubmitting( true );

		try {
			await submitApprovedBody( props.subscription, {
				address: approvedBody.address,
				coordinator: approvedBody.coordinator,
				deputy: approvedBody.deputy,
			} );
			setSubmitted( true );

			revalidate( "getUserSubscriptions" );
			
		} catch ( err ) {
			console.error( 'Error submitting approved body', err );
			setErrors( { submit: 'There was an error submitting your approved body. Please try again.' } );
		} finally {
			setSubmitting( false );
		}
	};

	if ( submitted() ) {
		return (
			<div class="text-center bg-green-50 border border-green-300 text-green-800 px-4 py-6 rounded-lg">
				<h3 class="text-lg font-semibold">Approved Body Submitted</h3>
				<p class="mt-2 text-sm">Thank you. We will verify the details and be in touch within 5 working days.</p>
			</div>
		);
	}

	return (
		<form class="w-full text-left" onSubmit={handleSubmit} noValidate>

			<div class="space-y-6">

				{/* Company Address */}
				<div>
					<h4 class="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">Company / Organisation</h4>
					<AddressEditor
						address={approvedBody.address}
						setAddress={( addr: Record<string, string> ) => setApprovedBody( "address", addr )} />
					{errors()['address'] && <p class="mt-1 text-sm text-red-600">{errors()['address']}</p>}
				</div>

				{/* Certification Co-ordinator */}
				<div>
					<h4 class="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">
						Certification Co-ordinator
					</h4>
					<div class="space-y-3">
						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="coordinator-name">Name</label>
							<div>
								<input
									type="text"
									id="coordinator-name"
									class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
									classList={{ 'border-red-500': !!errors()['coordinator.name'] }}
									value={approvedBody.coordinator.name}
									onInput={e => setApprovedBody( "coordinator", "name", e.currentTarget.value )} />
								{errors()['coordinator.name'] && <p class="mt-1 text-xs text-red-600">{errors()['coordinator.name']}</p>}
							</div>
						</div>

						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="coordinator-email">Email</label>
							<div>
								<input
									type="email"
									id="coordinator-email"
									class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
									classList={{ 'border-red-500': !!errors()['coordinator.email'] }}
									value={approvedBody.coordinator.email}
									onInput={e => setApprovedBody( "coordinator", "email", e.currentTarget.value )} />
								{errors()['coordinator.email'] && <p class="mt-1 text-xs text-red-600">{errors()['coordinator.email']}</p>}
							</div>
						</div>

						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="coordinator-telephone">Telephone</label>
							<div>
								<input
									type="tel"
									id="coordinator-telephone"
									class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
									classList={{ 'border-red-500': !!errors()['coordinator.telephone'] }}
									value={approvedBody.coordinator.telephone}
									onInput={e => setApprovedBody( "coordinator", "telephone", e.currentTarget.value )} />
								{errors()['coordinator.telephone'] && <p class="mt-1 text-xs text-red-600">{errors()['coordinator.telephone']}</p>}
							</div>
						</div>
					</div>
				</div>

				{/* Deputy Co-ordinator */}
				<div>
					<h4 class="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">
						Deputy Co-ordinator <span class="text-sm font-normal text-gray-500">(optional)</span>
					</h4>
					<div class="space-y-3">
						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="deputy-name">Name</label>
							<input
								type="text"
								id="deputy-name"
								class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
								value={approvedBody.deputy.name}
								onInput={e => setApprovedBody( "deputy", "name", e.currentTarget.value )} />
						</div>

						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="deputy-email">Email</label>
							<div>
								<input
									type="email"
									id="deputy-email"
									class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
									classList={{ 'border-red-500': !!errors()['deputy.email'] }}
									value={approvedBody.deputy.email}
									onInput={e => setApprovedBody( "deputy", "email", e.currentTarget.value )} />
								{errors()['deputy.email'] && <p class="mt-1 text-xs text-red-600">{errors()['deputy.email']}</p>}
							</div>
						</div>

						<div class="grid grid-cols-[140px_1fr] items-center gap-3">
							<label class="text-sm font-medium text-gray-700 text-right" for="deputy-telephone">Telephone</label>
							<input
								type="tel"
								id="deputy-telephone"
								class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
								value={approvedBody.deputy.telephone}
								onInput={e => setApprovedBody( "deputy", "telephone", e.currentTarget.value )} />
						</div>
					</div>
				</div>

				{errors()['submit'] && (
					<p class="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{errors()['submit']}</p>
				)}

				<div class="flex justify-end pt-2">
					<button
						type="submit"
						disabled={submitting()}
						class="rias bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
						{submitting() ? 'Submitting…' : 'Submit Approved Body'}
					</button>
				</div>

			</div>
		</form>
	);
}
